import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Column } from "@tanstack/react-table";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  CalendarDays as CalendarKindIcon,
  CaseSensitiveIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EyeOffIcon,
  FunnelPlusIcon,
  HashIcon,
  ListChecksIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { Creator, OrgCreatorsFacets } from "@/types/types";
import { useCreatorsTableContext } from "../CreatorsTableContext";
import {
  ColumnFilterKind,
  ColumnFilterMeta,
  DateRangeFilter,
  NumberRangeFilter,
  formatFilterCompact,
  formatFilterInput,
  formatIsoDay,
  optionLabel,
  parseFilterNumber,
  parseIsoDay,
  selectKey,
  summarizeFilter,
} from "./columnFilters";

type FilterColumn = Column<Creator, unknown>;

interface EditorProps {
  column: FilterColumn;
  meta: ColumnFilterMeta;
}

const KIND_ICONS: Record<ColumnFilterKind, typeof HashIcon> = {
  text: CaseSensitiveIcon,
  number: HashIcon,
  select: ListChecksIcon,
  date: CalendarKindIcon,
};

// cmdk matches on `value` by default (our column ids / option keys); only the
// human labels we pass as keywords should count.
const keywordFilter = (_value: string, search: string, keywords?: string[]) =>
  (keywords ?? []).join(" ").toLowerCase().includes(search.toLowerCase()) ? 1 : 0;

function filterMeta(column: FilterColumn): ColumnFilterMeta | undefined {
  return column.columnDef.meta?.filter;
}

// Checklist columns whose option counts the server facets carry (org-wide);
// every other select column (CRM booleans) falls back to meta.options.
const SERVER_FACET_COLUMNS = new Set([
  "status",
  "campaign",
  "inbox",
  "country",
  "gender",
  "age",
  "language",
  "current_tier_order",
]);

function serverFacetCounts(
  facets: OrgCreatorsFacets | null,
  columnId: string
): Record<string, number> | undefined {
  if (!facets || !SERVER_FACET_COLUMNS.has(columnId)) return undefined;
  return (facets as unknown as Record<string, Record<string, number> | undefined>)[columnId];
}

/**
 * Icon-only toolbar button → popover listing every column that declared a
 * `meta.filter`; picking one swaps the popover to that column's editor
 * (text / number range / checklist). Filters stack across columns and apply
 * to hidden columns too; the toolbar "Clear" wipes them with the rest.
 */
// Pane swap — mirrors the "Page side-by-side" tokens in index.css
// (--page-slide-distance 8px, --page-blur 3px, --page-slide-ease).
const SWAP_DISTANCE = 8;
const SWAP_BLUR = 3;
const SWAP_EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];
const swapVariants = {
  enter: (dir: number) => ({
    opacity: 0,
    x: dir * SWAP_DISTANCE,
    filter: `blur(${SWAP_BLUR}px)`,
  }),
  center: { opacity: 1, x: 0, filter: "blur(0px)" },
  exit: (dir: number) => ({
    opacity: 0,
    x: -dir * SWAP_DISTANCE,
    filter: `blur(${SWAP_BLUR}px)`,
  }),
};

interface View {
  /** Column being edited; null for the column list. */
  id: string | null;
  /** 1 = forward (list → editor), -1 = back. Drives the slide direction. */
  dir: 1 | -1;
}

export default function ColumnFilter() {
  const { table } = useCreatorsTableContext();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>({ id: null, dir: 1 });
  const showList = () => setView({ id: null, dir: -1 });
  const showEditor = (id: string) => setView({ id, dir: 1 });

  const columns = table
    .getAllLeafColumns()
    .filter((column) => !!filterMeta(column) && column.getCanFilter());
  const active = view.id ? (columns.find((c) => c.id === view.id) ?? null) : null;
  const activeCount = columns.filter((c) => c.getFilterValue() !== undefined).length;

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        // Reset on open, not close: the content stays mounted through the
        // popover's close animation and would visibly swap panes mid-fade.
        if (next) setView({ id: null, dir: 1 });
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className="h-7.5 px-2 hover:bg-muted-foreground/10 [&>svg]:opacity-60 hover:[&>svg]:opacity-100 data-[state=open]:bg-muted data-[state=open]:[&>svg]:opacity-100"
          aria-label="Filter by column"
          title="Filter by column"
        >
          <FunnelPlusIcon size={16} aria-hidden="true" />
          {activeCount > 0 && (
            <Badge variant="count" className="-me-1">
              {activeCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-64 overflow-hidden p-0"
        align="start"
        // Esc inside a column's editor steps back to the list; from the list
        // it closes the popover as usual.
        onEscapeKeyDown={(e) => {
          if (!view.id) return;
          e.preventDefault();
          showList();
        }}
      >
        <SwapFrame paneKey={view.id ?? "list"} direction={view.dir}>
          {active ? (
            <ColumnEditor column={active} onBack={showList} />
          ) : (
            <ColumnList columns={columns} onPick={showEditor} />
          )}
        </SwapFrame>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Swaps the popover's pane with a blur + side-by-side slide (forward = the
 * new pane comes in from the right, back = from the left) and eases the
 * popover's height to the incoming pane instead of jumping.
 */
function SwapFrame({
  paneKey,
  direction,
  children,
}: {
  paneKey: string;
  direction: number;
  children: ReactNode;
}) {
  const reduceMotion = useReducedMotion();
  const measureRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | "auto">("auto");

  useEffect(() => {
    const el = measureRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) =>
      setHeight(entry.contentRect.height)
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <motion.div
      animate={{ height }}
      transition={{ duration: reduceMotion ? 0 : 0.2, ease: SWAP_EASE }}
      style={{ overflow: "clip" }}
    >
      <div ref={measureRef}>
        <AnimatePresence mode="wait" initial={false} custom={direction}>
          <motion.div
            key={paneKey}
            custom={direction}
            variants={swapVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: reduceMotion ? 0 : 0.15, ease: SWAP_EASE }}
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

function ColumnList({
  columns,
  onPick,
}: {
  columns: FilterColumn[];
  onPick: (id: string) => void;
}) {
  return (
    <Command loop filter={keywordFilter}>
      <CommandInput autoFocus placeholder="Search columns…" className="h-9 text-xs" />
      <CommandList className="max-h-86">
        <CommandEmpty className="py-4 text-xs text-muted-foreground">
          No column found.
        </CommandEmpty>
        <CommandGroup>
          {columns.map((column) => {
            const meta = filterMeta(column)!;
            const Icon = KIND_ICONS[meta.kind];
            const summary = summarizeFilter(meta, column.getFilterValue());
            return (
              <CommandItem
                key={column.id}
                value={column.id}
                keywords={[meta.label]}
                onSelect={() => onPick(column.id)}
                className="gap-2 py-1.5 text-xs h-8 rounded-sm"
              >
                <Icon className="size-3.75 shrink-0" />
                <span className="truncate">{meta.label}</span>
                {!column.getIsVisible() && (
                  <EyeOffIcon
                    className="size-3 shrink-0 text-muted-foreground/60"
                    aria-label="Hidden column"
                  />
                )}
                {summary ? (
                  <span className="ml-auto max-w-24 truncate font-mono text-[10px] text-primary">
                    {summary}
                  </span>
                ) : (
                  <ChevronRightIcon className="ml-auto size-3 shrink-0 text-muted-foreground/50" />
                )}
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}

function ColumnEditor({ column, onBack }: { column: FilterColumn; onBack: () => void }) {
  const meta = filterMeta(column)!;
  const Icon = KIND_ICONS[meta.kind];
  const hasValue = column.getFilterValue() !== undefined;

  return (
    <div className="flex flex-col">
      <div className="flex h-9 items-center gap-1 border-b pl-1 pr-2">
        <Button
          variant="ghost"
          size="icon-sm"
          scope={false}
          className="shrink-0 rounded-sm"
          onClick={onBack}
          aria-label="Back to columns"
        >
          <ChevronLeftIcon />
        </Button>
        <Icon className="size-3.5 shrink-0 text-muted-foreground" />
        <span className="truncate text-xs font-medium">{meta.label}</span>
        {hasValue && (
          <Button
            variant="destructive-ghost"
            size="sm"
            className="ml-auto h-5 px-1.5 text-[11px]"
            onClick={() => column.setFilterValue(undefined)}
          >
            Clear
          </Button>
        )}
      </div>
      {meta.kind === "text" && <TextEditor column={column} meta={meta} />}
      {meta.kind === "number" && <NumberEditor column={column} meta={meta} />}
      {meta.kind === "select" && <SelectEditor column={column} meta={meta} />}
      {meta.kind === "date" && <DateEditor column={column} meta={meta} />}
    </div>
  );
}

function TextEditor({ column, meta }: EditorProps) {
  const value = (column.getFilterValue() as string | undefined) ?? "";
  return (
    <div className="p-2">
      <Input
        autoFocus
        value={value}
        onChange={(e) => column.setFilterValue(e.target.value)}
        placeholder={meta.placeholder ?? "Contains…"}
        className="h-8 text-xs"
        aria-label={`Filter ${meta.label}`}
      />
    </div>
  );
}

function NumberEditor({ column, meta }: EditorProps) {
  const { table, serverFacets } = useCreatorsTableContext();
  const scale = meta.scale ?? 1;
  const value = column.getFilterValue() as NumberRangeFilter | undefined;
  const toText = (n?: number) => (n == null ? "" : formatFilterInput(n * scale));
  const [minText, setMinText] = useState(() => toText(value?.min));
  const [maxText, setMaxText] = useState(() => toText(value?.max));
  const lastCommitted = useRef<NumberRangeFilter | undefined>(value);

  // Cleared from outside (header/toolbar Clear) → empty the inputs too. Our
  // own commits update lastCommitted first, so half-typed input ("1." or
  // "k") that parses to nothing doesn't wipe what the user is typing.
  useEffect(() => {
    if (value === undefined && lastCommitted.current !== undefined) {
      lastCommitted.current = undefined;
      setMinText("");
      setMaxText("");
    }
  }, [value]);

  const commit = (nextMin: string, nextMax: string) => {
    const parse = (text: string) => {
      const n = parseFilterNumber(text);
      return n == null ? undefined : n / scale;
    };
    const next: NumberRangeFilter = { min: parse(nextMin), max: parse(nextMax) };
    const empty = next.min == null && next.max == null;
    lastCommitted.current = empty ? undefined : next;
    column.setFilterValue(empty ? undefined : next);
  };

  // The column's actual span as placeholders, so the user knows the scale —
  // org-wide from the server's bounds (the loaded page is only a slice).
  const preFiltered = table.getPreFilteredRowModel();
  const serverBounds = serverFacets?.bounds?.[column.id];
  const [dataMin, dataMax] = useMemo(() => {
    if (serverFacets) {
      return [serverBounds?.min ?? undefined, serverBounds?.max ?? undefined];
    }
    let lo = Infinity;
    let hi = -Infinity;
    for (const row of preFiltered.flatRows) {
      const raw = row.getValue(column.id);
      const n =
        typeof raw === "number" ? raw : raw == null || raw === "" ? NaN : Number(raw);
      if (Number.isNaN(n)) continue;
      if (n < lo) lo = n;
      if (n > hi) hi = n;
    }
    return lo === Infinity ? [undefined, undefined] : [lo, hi];
  }, [preFiltered, column.id, serverFacets, serverBounds]);

  return (
    <div className="space-y-1.5 p-2">
      <div className="flex items-center gap-1.5">
        <Input
          autoFocus
          value={minText}
          onChange={(e) => {
            setMinText(e.target.value);
            commit(e.target.value, maxText);
          }}
          placeholder={dataMin != null ? `min ${formatFilterCompact(dataMin * scale)}` : "min"}
          inputMode="decimal"
          className="h-8 font-mono text-xs"
          aria-label={`${meta.label} minimum`}
        />
        <span className="text-xs text-muted-foreground">–</span>
        <Input
          value={maxText}
          onChange={(e) => {
            setMaxText(e.target.value);
            commit(minText, e.target.value);
          }}
          placeholder={dataMax != null ? `max ${formatFilterCompact(dataMax * scale)}` : "max"}
          inputMode="decimal"
          className="h-8 font-mono text-xs"
          aria-label={`${meta.label} maximum`}
        />
        {meta.unit && (
          <span className="shrink-0 text-[10px] text-muted-foreground">{meta.unit}</span>
        )}
      </div>
      <p className="text-[10px] text-muted-foreground">
        Inclusive · shorthand ok (10k, 1.5m)
      </p>
    </div>
  );
}

function SelectEditor({ column, meta }: EditorProps) {
  const { serverFacets } = useCreatorsTableContext();
  const facets = column.getFacetedUniqueValues();
  const serverCounts = serverFacetCounts(serverFacets, column.id);
  const options = useMemo(() => {
    // Facets are raw values; fold null/undefined/"" into one "Missing" row.
    // Org-wide the loaded page is only a slice: the server's org-wide counts
    // (or, for columns it doesn't facet, the column's declared options with
    // no count) replace the page facets.
    const counts = new Map<string, number | undefined>();
    if (serverFacets) {
      if (serverCounts) {
        for (const [key, count] of Object.entries(serverCounts)) {
          counts.set(key, (counts.get(key) ?? 0) + count);
        }
      } else {
        for (const key of meta.options ?? []) counts.set(key, undefined);
      }
    } else {
      for (const [raw, count] of facets) {
        const key = selectKey(raw);
        counts.set(key, (counts.get(key) ?? 0) + count);
      }
    }
    // An explicit order (meta.order) wins; the rest is alphabetical, with
    // "Missing" last (empty facet or a CRM boolean's own key).
    const rank = new Map((meta.order ?? []).map((key, i) => [key, i]));
    const position = (key: string) => rank.get(key) ?? Number.MAX_SAFE_INTEGER;
    return Array.from(counts, ([key, count]) => ({
      key,
      count,
      label: optionLabel(meta, key),
    })).sort(
      (a, b) =>
        Number(a.label === "Missing") - Number(b.label === "Missing") ||
        position(a.key) - position(b.key) ||
        a.label.localeCompare(b.label)
    );
  }, [facets, meta, serverFacets, serverCounts]);

  const selected = (column.getFilterValue() as string[] | undefined) ?? [];
  const toggle = (key: string) => {
    const next = selected.includes(key)
      ? selected.filter((k) => k !== key)
      : [...selected, key];
    column.setFilterValue(next.length ? next : undefined);
  };
  const searchable = options.length > 7;

  return (
    <Command loop filter={keywordFilter}>
      {searchable && (
        <CommandInput
          autoFocus
          placeholder={`Search ${meta.label.toLowerCase()}…`}
          className="h-9 text-xs"
        />
      )}
      <CommandList className="max-h-64">
        <CommandEmpty className="py-4 text-xs text-muted-foreground">No match.</CommandEmpty>
        <CommandGroup>
          {options.map((option) => (
            <CommandItem
              key={option.key}
              value={option.key || "__missing__"}
              keywords={[option.label]}
              onSelect={() => toggle(option.key)}
              className="gap-2 py-1.5 pr-3 text-xs h-8 rounded-sm"
            >
              <Checkbox
                checked={selected.includes(option.key)}
                tabIndex={-1}
                aria-hidden="true"
                className="pointer-events-none"
              />
              <span
                className={cn("truncate", option.key === "" && "italic text-muted-foreground")}
              >
                {option.label}
              </span>
              {option.count !== undefined && (
                <span className="ml-auto font-mono text-[10px] text-muted-foreground">
                  {option.count}
                </span>
              )}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}

function DateEditor({ column, meta }: EditorProps) {
  const value = column.getFilterValue() as DateRangeFilter | undefined;
  const from = parseIsoDay(value?.from);
  const to = parseIsoDay(value?.to);
  const selected = from || to ? { from: from ?? to, to } : undefined;

  return (
    <div className="flex flex-col items-center">
      <Calendar
        mode="range"
        captionLayout="dropdown"
        defaultMonth={from ?? to}
        selected={selected}
        onSelect={(range) => {
          const next: DateRangeFilter = {
            from: range?.from ? formatIsoDay(range.from) : undefined,
            to: range?.to ? formatIsoDay(range.to) : undefined,
          };
          column.setFilterValue(next.from || next.to ? next : undefined);
        }}
        autoFocus
      />
      <p className="w-full px-3 pb-2 text-[10px] text-muted-foreground">
        {summarizeFilter(meta, value) ?? "Pick a first day, then a last day"}
      </p>
    </div>
  );
}
